<template>
  <div ref="ctaContainer" class="grid-y">
    <LowAvailability :product-id="productId" />
    <Notification v-if="error" severity="error" class="margin-bottom-m" aria-live="assertive" role="alert">
      <span v-html="error.message"></span>
    </Notification>

    <UpdateBag v-if="isUpdateBagEnabled" :payload="atb.payload.value" :payload-validator="atb.payloadValidator" @error="errorHandler" />
    <template v-else>
      <LazyLoadWhenOutOfViewport scroll-direction="down" :enable-intersection-observer="isStickyAtbCompActive" :force-show="forcedStickyRef">
        <template #main>
          <div
            class="cta-buttons"
            :class="{
              'desktop-inline': shoppableRedesignOptimization && props.buttonsInline,
              inline: isCtaPlacementInline,
              swapped: isCtaPlacementInlineSwapped,
            }"
          >
            <AddToBag :payload="atb.payload.value" :payload-validator="atb.payloadValidator" @error="errorHandler">
              <span> Add To Bag </span>
            </AddToBag>
            <BuyNow v-if="buyNow" :payload="atb.payload.value" :payload-validator="atb.payloadValidator" @error="errorHandler" />
          </div>
        </template>
        <template #out-of-viewport="{ show }">
          <ClientOnly>
            <StickyAtbFooter v-if="isStickyAtbCompActive" :atb :show @cta-errors="errorHandler" @scroll-to="scrollToFromStickyFooter" />
          </ClientOnly>
        </template>
      </LazyLoadWhenOutOfViewport>
    </template>
    <AddToRegistry v-if="isAddToRegistryEligible" :payload="atr.payload.value" :payload-validator="atr.payloadValidator" @error="errorHandler" />
    <div v-if="showCallCenterBanner" class="margin-top-s" data-test-id="call-center-message" v-html="callCenterMessage"></div>
  </div>
</template>

<script setup>
import { shallowRef, defineAsyncComponent, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { fulfillmentErrors } from 'config';
import Notification from '@atomic-ui/notification/Notification';

import LowAvailability from '~/modules/availability/components/LowAvailability';
import AddToBag from '~/modules/add-to-bag/components/AddToBag';
import BuyNow from '~/modules/add-to-bag/components/BuyNow';
import UpdateBag from '~/modules/add-to-bag/components/UpdateBag';
import { useATBPayload } from '~/modules/add-to-bag/composables/useATBPayload';
import { useBagContext } from '~/modules/add-to-bag/composables/useBagContext';

import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import AddToRegistry from '~/modules/add-to-registry/components/AddToRegistry';
import { useATRPayload } from '~/modules/add-to-registry/composables/useATRPayload';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import LazyLoadWhenOutOfViewport from '~/components/common/LazyLoadWhenOutOfViewport.vue';
import { useProductType } from '~/composables/useProductType';
import { useTraitsErrorActive } from '~/modules/sticky-product-traits/composables/context-providers/useTraitsErrorActive';
import useExperiments from '~/stores/useExperimentsStore';
import useIsolatedStickyAtbStateStore from '~/modules/sticky-product-traits/stores/useIsolatedStickyAtbStateStore';

import { useCallCenter } from '~/composables/useCallCenter';

const SCROLL_CONFIG = { behavior: 'smooth', block: 'center' };

const StickyAtbFooter = defineAsyncComponent(() => import('~/modules/sticky-product-traits/components/StickyAtbFooter.vue'));

const {
  experimentFlags: { isCtaPlacementInline, isCtaPlacementInlineSwapped },
} = useExperiments();

/**
 * @type {import('vue').ShallowRef<Nullable<Error>>}
 */
const error = shallowRef(null);

const props = defineProps({
  atbPayload: {
    /** @type {import('vue').PropType<Partial<import('~/modules/add-to-bag/types').ATBPayloadItem>>} */
    type: Object,
    default: () => ({}),
  },
  buttonsInline: {
    type: Boolean,
    default: true,
  },
  stickyFooterEnabled: {
    type: Boolean,
    default: true,
  },
});

const ctaContainerRef = useTemplateRef('ctaContainer');

const { productId } = useProduct();
const { pageLevelFeatureFlags: { isStickyAtbEnabled = false } = {} } = useProductType();

const isStickyAtbCompActive = props.stickyFooterEnabled && isStickyAtbEnabled;
const { addToRegistry, buyNow, shoppableRedesignOptimization, isShopTheSetExpEnabled } = useFeatureEligibility();
const { memberFeatureEligible } = storeToRefs(useConsolidatedMemberStore());
const { forcedStickyRef } = storeToRefs(useIsolatedStickyAtbStateStore());
const isAddToRegistryEligible = computed(() => (isShopTheSetExpEnabled ? memberFeatureEligible.value?.addToRegistry : addToRegistry));
const { isUpdateBagEnabled } = useBagContext();
const { errorTextActive } = useTraitsErrorActive();

const atb = useATBPayload({
  additionalPayload: () => props.atbPayload,
});
const atr = useATRPayload();

const { showCallCenterBanner, callCenterMessage } = useCallCenter(true);

/**
 *
 * @param {Error|any} err
 */
const errorHandler = (err) => {
  if (Object.values(fulfillmentErrors).includes(err?.message)) {
    return;
  }
  error.value = err;
};
const scrollToFromStickyFooter = ({ scrollToTraits = false } = {}) => {
  if (scrollToTraits) {
    if (!errorTextActive.value) {
      errorTextActive.value = true;
    }
    document.body.querySelector('.product-traits-wrapper')?.scrollIntoView(SCROLL_CONFIG);
  } else {
    // scrolls to cta container
    ctaContainerRef.value?.scrollIntoView(SCROLL_CONFIG);
  }
};
</script>
